// frontend/src/components/DeleteConfirmationModal.js
import React from 'react';
import { AlertTriangle } from 'lucide-react';
import '../styles/DeleteConfirmationModal.css'; // モーダル用のCSSをインポート

/**
 * 削除確認モーダルコンポーネント
 *
 * @param {object} props
 * @param {boolean} props.isOpen - モーダルが開いているかどうか
 * @param {function} props.onClose - モーダルを閉じる関数
 * @param {function} props.onConfirm - 削除実行を確認する関数
 * @param {object | null} props.reportData - 削除対象のレポートデータ { hospital_name, department_name, ... }
 * @param {object} props.formData - フォームの入力値 { hospitalName, departmentName, userName }
 * @param {function} props.onFormChange - フォーム入力が変更されたときの関数
 * @param {boolean} props.isDeleting - 削除処理中かどうかのフラグ
 * @param {string} props.error - 表示するエラーメッセージ
 * @param {object | null} props.currentUser - 現在のログインユーザー情報 (ユーザー名表示用)
 * @param {number} props.step - 確認ステップ (1: 入力確認, 2: 最終確認)
 * @param {function} props.onBack - ステップ1に戻る関数
 * @param {string} props.deleteToken - 削除確認用ランダムトークン
 * @param {string} props.deleteTokenInput - ユーザーが入力したトークン
 * @param {function} props.onTokenInputChange - トークン入力変更ハンドラ
 */
function DeleteConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  reportData,
  formData,
  onFormChange,
  isDeleting,
  error,
  currentUser,
  step = 1,
  onBack,
  deleteToken,
  deleteTokenInput,
  onTokenInputChange
}) {
  // モーダルが開いていない、または削除対象データがない場合は何もレンダリングしない
  if (!isOpen || !reportData) {
    return null;
  }

  // コピー・ペーストを防止するハンドラ
  const preventCopyPaste = (e) => {
    e.preventDefault();
    return false;
  };

  // 右クリックを防止するハンドラ
  const preventContextMenu = (e) => {
    e.preventDefault();
    return false;
  };
  
  // フォームが全て入力されているかチェック (単純な空文字チェック)
  const isFormFilled =
    formData.hospitalName.trim() !== '' &&
    formData.departmentName.trim() !== '' &&
    formData.userName.trim() !== '';

  // トークンが正しく入力されているかチェック
  const isTokenMatch = deleteTokenInput.trim() === deleteToken;

  // ステップ1: 入力確認画面
  if (step === 1) {
    return (
      <div className="delete-modal-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="modal-title">
        <div className="delete-modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="delete-modal-header">
            <h2 id="modal-title" className="delete-modal-title">レポート削除確認</h2>
            <button onClick={onClose} className="delete-modal-close-button" aria-label="閉じる" disabled={isDeleting}>
              ×
            </button>
          </div>

          <div className="delete-modal-body">
            <p>
              以下のレポートを削除しますか？<br />
              削除するには、表示されている病院名、科名、およびご自身のユーザー名を入力してください。
            </p>
            <div className="delete-modal-report-info">
              <p><strong>病院名:</strong> {reportData.hospital_name}</p>
              <p><strong>科名:</strong> {reportData.department_name}</p>
              {currentUser && <p><small>(あなたのユーザー名: {currentUser.username})</small></p>}
            </div>

            <form className="delete-modal-form" onSubmit={(e) => { e.preventDefault(); onConfirm(); }}>
              <div className="delete-modal-form-group">
                <label htmlFor="delete-hospitalName">病院名</label>
                <input
                  type="text"
                  id="delete-hospitalName"
                  name="hospitalName"
                  value={formData.hospitalName}
                  onChange={onFormChange}
                  required
                  disabled={isDeleting}
                  aria-describedby={error ? "delete-error-message" : undefined}
                />
              </div>
              <div className="delete-modal-form-group">
                <label htmlFor="delete-departmentName">科名</label>
                <input
                  type="text"
                  id="delete-departmentName"
                  name="departmentName"
                  value={formData.departmentName}
                  onChange={onFormChange}
                  required
                  disabled={isDeleting}
                  aria-describedby={error ? "delete-error-message" : undefined}
                />
              </div>
              <div className="delete-modal-form-group">
                <label htmlFor="delete-userName">ユーザー名</label>
                <input
                  type="text"
                  id="delete-userName"
                  name="userName"
                  value={formData.userName}
                  onChange={onFormChange}
                  required
                  disabled={isDeleting}
                  aria-describedby={error ? "delete-error-message" : undefined}
                />
              </div>
              {error && <p id="delete-error-message" className="delete-modal-error" role="alert">{error}</p>}
            </form>
          </div>

          <div className="delete-modal-footer">
            <button
              className="delete-modal-button delete-modal-cancel-button"
              onClick={onClose}
              disabled={isDeleting}
            >
              キャンセル
            </button>
            <button
              className="delete-modal-button delete-modal-confirm-button"
              onClick={onConfirm}
              disabled={isDeleting || !isFormFilled}
            >
              次へ
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ステップ2: 最終確認画面（トークン入力）
  return (
    <div className="delete-modal-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="modal-title">
      <div className="delete-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="delete-modal-header">
          <h2 id="modal-title" className="delete-modal-title">最終確認</h2>
          <button onClick={onClose} className="delete-modal-close-button" aria-label="閉じる" disabled={isDeleting}>
            ×
          </button>
        </div>

        <div className="delete-modal-body">
          <div className="delete-modal-warning">
            <AlertTriangle size={48} className="delete-modal-warning-icon" />
            <p className="delete-modal-warning-text">
              <strong>本当に削除しますか？</strong>
            </p>
            <p className="delete-modal-warning-subtext">
              この操作は取り消すことができません。
            </p>
          </div>

          <div className="delete-modal-report-info">
            <p><strong>病院名:</strong> {reportData.hospital_name}</p>
            <p><strong>科名:</strong> {reportData.department_name}</p>
          </div>

          {/* 確認コード入力セクション */}
          <div className="delete-modal-token-section">
            <p className="delete-modal-token-instruction">
              削除を実行するには、以下の確認コードを入力してください。
            </p>
            <div 
              className="delete-modal-token-display"
              onCopy={preventCopyPaste}
              onCut={preventCopyPaste}
              onContextMenu={preventContextMenu}
            >
              <span className="delete-modal-token-label">確認コード:</span>
              <span 
                className="delete-modal-token-value"
                style={{ userSelect: 'none', WebkitUserSelect: 'none', MozUserSelect: 'none', msUserSelect: 'none' }}
              >
                {deleteToken}
              </span>
            </div>
            <div className="delete-modal-form-group">
              <label htmlFor="delete-token-input">確認コードを入力</label>
              <input
                type="text"
                id="delete-token-input"
                value={deleteTokenInput}
                onChange={onTokenInputChange}
                onPaste={preventCopyPaste}
                onCopy={preventCopyPaste}
                onCut={preventCopyPaste}
                onContextMenu={preventContextMenu}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
                placeholder="上記のコードを手入力してください"
                disabled={isDeleting}
                className="delete-modal-token-input"
              />
            </div>
          </div>

          {error && <p className="delete-modal-error" role="alert">{error}</p>}
        </div>

        <div className="delete-modal-footer">
          <button
            className="delete-modal-button delete-modal-cancel-button"
            onClick={onBack}
            disabled={isDeleting}
          >
            戻る
          </button>
          <button
            className="delete-modal-button delete-modal-confirm-button"
            onClick={onConfirm}
            disabled={isDeleting || !isTokenMatch}
          >
            {isDeleting ? (
              <>
                <span className="delete-modal-spinner" aria-hidden="true"></span>
                削除中...
              </>
            ) : '削除を実行する'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default DeleteConfirmationModal;